// The browser side of passkeys: turns the console's options into a Face ID /
// fingerprint / PIN prompt, and the answer back into plain text for the server.

const toBytes = (s: string): ArrayBuffer => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;
};
const toText = (b: ArrayBuffer): string => {
  let s = '';
  for (const byte of new Uint8Array(b)) s += String.fromCharCode(byte);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeysAvailable = (): boolean => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;

export interface CreateOptions {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  authenticatorSelection: AuthenticatorSelectionCriteria;
  timeout: number;
}

export interface GetOptions {
  challenge: string;
  rpId: string;
  timeout: number;
  allowCredentials?: { type: 'public-key'; id: string }[];
}

export async function createPasskey(o: CreateOptions) {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: toBytes(o.challenge),
      rp: o.rp,
      user: { id: toBytes(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams,
      authenticatorSelection: o.authenticatorSelection,
      attestation: 'none',
      timeout: o.timeout,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was made.');
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    clientDataJSON: toText(r.clientDataJSON),
    attestationObject: toText(r.attestationObject),
    transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
  };
}

export async function usePasskey(o: GetOptions) {
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge: toBytes(o.challenge),
      rpId: o.rpId,
      allowCredentials: (o.allowCredentials ?? []).map((c) => ({ type: 'public-key' as const, id: toBytes(c.id) })),
      userVerification: 'required',
      timeout: o.timeout,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('No passkey was used.');
  const r = cred.response as AuthenticatorAssertionResponse;
  return { id: cred.id, clientDataJSON: toText(r.clientDataJSON), authenticatorData: toText(r.authenticatorData), signature: toText(r.signature) };
}

/** Browsers report a cancelled prompt as NotAllowedError: say so in plain words. */
export function passkeyProblem(e: unknown): string {
  const name = (e as { name?: string })?.name;
  if (name === 'NotAllowedError') return 'The passkey prompt was cancelled or timed out. Please try again.';
  if (name === 'InvalidStateError') return 'This device already has a passkey for this. Try signing in instead.';
  return e instanceof Error ? e.message : 'The passkey step failed. Please try again.';
}
